// Polygon/line path mutators — pure (state, args) => patch.

import { pathSegments, insertVertexNear, insertVertexAt, segmentEntry, setSegmentTypeAt } from '../segments';
import { straightPathSides, setStraightSideLength } from '../../path_sides';

// Catmull-Rom spline through the anchor points, converted to cubic beziers, so
// the path visibly bends and every segment exposes two draggable control
// points. Polygons close smoothly with a ['closing','bezier',...] entry.
export function smoothCoordinates(coordinates, closed) {
  const pts = (coordinates || []).filter((c) => Array.isArray(c) && typeof c[0] === 'number').map((c) => [c[0], c[1]]);
  if (pts.length < 2) return null;
  const n = pts.length;
  const at = (i) => (closed ? pts[((i % n) + n) % n] : pts[Math.max(0, Math.min(n - 1, i))]);
  const out = [[pts[0][0], pts[0][1]]];
  const segs = closed ? n : n - 1;
  for (let i = 0; i < segs; i += 1) {
    const p0 = at(i - 1); const p1 = at(i); const p2 = at(i + 1); const p3 = at(i + 2);
    const c1x = p1[0] + (p2[0] - p0[0]) / 6; const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6; const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    if (closed && i === segs - 1) out.push(['closing', 'bezier', c1x, c1y, c2x, c2y]);
    else out.push([p2[0], p2[1], 'bezier', c1x, c1y, c2x, c2y]);
  }
  return out;
}

// Drop all curve/bezier control data (and any explicit close) — straight only.
export function straightenCoordinates(coordinates) {
  return (coordinates || [])
    .filter((c) => Array.isArray(c) && typeof c[0] === 'number')
    .map((c) => [c[0], c[1]]);
}

// Locked paths are never reshaped.
function patchPath(state, id, fn) {
  let hit = false;
  const elements = state.elements.map((el) => {
    if (el.id !== id || (el.type !== 'polygon' && el.type !== 'line') || el.data?.locked) return el;
    const coordinates = fn(el);
    if (!coordinates) return el;
    hit = true;
    return { ...el, data: { ...el.data, coordinates } };
  });
  return hit ? { elements } : null;
}

export function smoothPath(state, { id }) {
  return patchPath(state, id, (el) => smoothCoordinates(el.data.coordinates, el.type === 'polygon'));
}

export function straightenPath(state, { id }) {
  return patchPath(state, id, (el) => straightenCoordinates(el.data.coordinates));
}

// Insert a vertex at the point on the path nearest (x,y) — curved segments are
// split with de Casteljau so the curve keeps its exact shape.
export function addVertex(state, { id, x, y }) {
  return dropPick(patchPath(state, id, (el) => insertVertexNear(el.data.coordinates, el.type === 'polygon', x, y)));
}

// Adding or removing a point renumbers the segments: drop a picked one.
const dropPick = (patch) => (patch ? { ...patch, selectedSegment: null } : null);

// Insert a vertex in the middle of the segment at coordinate index `index`.
export function addVertexAt(state, { id, index }) {
  return dropPick(patchPath(state, id, (el) => insertVertexAt(el.data.coordinates, el.type === 'polygon', index)?.coordinates));
}

// Remove an anchor (never below two). Curve controls on the removed entry go
// with it; the following segment keeps its own.
export function removeVertex(state, { id, index }) {
  return dropPick(patchPath(state, id, (el) => {
    const coords = el.data.coordinates || [];
    const entry = coords[index];
    if (!Array.isArray(entry) || typeof entry[0] !== 'number') return null;
    if (coords.filter((c) => Array.isArray(c) && typeof c[0] === 'number').length <= 2) return null;
    return coords.filter((_, i) => i !== index);
  }));
}

// --- per-segment curve type ------------------------------------------------
// Smooth one segment into a cubic using its neighbouring anchors for tangents
// (the same Catmull-Rom construction whole-shape smoothing uses), so a single
// edge can bend without reshaping the rest of the path.
export function smoothSegment(state, { id, index }) {
  return patchPath(state, id, (el) => {
    const closed = el.type === 'polygon';
    const coords = el.data.coordinates || [];
    const segs = pathSegments(coords, closed);
    const at = segs.findIndex((s) => s.index === index);
    if (at < 0 || segs[at].implicit) return null;
    const seg = segs[at];
    const prev = segs[at - 1]?.from ?? (closed ? segs[segs.length - 1].from : seg.from);
    const next = segs[at + 1]?.to ?? (closed ? segs[0].to : seg.to);
    const c1 = [seg.from[0] + (seg.to[0] - prev[0]) / 6, seg.from[1] + (seg.to[1] - prev[1]) / 6];
    const c2 = [seg.to[0] - (next[0] - seg.from[0]) / 6, seg.to[1] - (next[1] - seg.from[1]) / 6];
    const out = coords.slice();
    out[index] = segmentEntry('cubic', [c1, c2], seg.to, !!seg.closing);
    return out;
  });
}

// Set one segment's type (straight / curve / bezier), with v1's default
// controls for a new curve.
export function setSegmentType(state, { id, index, type }) {
  return patchPath(state, id, (el) => setSegmentTypeAt(el.data.coordinates, el.type === 'polygon', index, type));
}

// Drop one segment's curve controls — that edge becomes straight.
export function straightenSegment(state, { id, index }) {
  return patchPath(state, id, (el) => {
    const coords = el.data.coordinates || [];
    const segs = pathSegments(coords, el.type === 'polygon');
    const seg = segs.find((s) => s.index === index);
    if (!seg || seg.implicit || seg.kind === 'line') return null;
    const out = coords.slice();
    out[index] = segmentEntry('line', [], seg.to, !!seg.closing);
    return out;
  });
}

// Type an exact length (plan units) for one straight side — `side` indexes
// straightPathSides(). The side's end anchor moves along its current direction.
export function setSideLength(state, { id, side, length }) {
  return patchPath(state, id, (el) => {
    const coordinates = (el.data.coordinates || []).map((c) => (Array.isArray(c) ? c.slice() : c));
    const target = straightPathSides(coordinates, el.type === 'polygon')[side];
    return setStraightSideLength(coordinates, target, length) ? coordinates : null;
  });
}
